/**
 * Atlas Advisor suggested questions: URL tab/subtab params to API full-name mapping.
 * Canonical list matches DynamoDB keys (see plan: Tab / Sub tab full names).
 * We keep both (tab, subtab) and (tabFullName, subTabFullName) to track the exact mapping.
 */

export interface TabSubtabMapping {
  /** URL query param (e.g. trafficV2, scorecardV2) */
  tab: string;
  /** URL query param (e.g. totalClicks, all) */
  subtab: string;
  /** DynamoDB PK / API param (e.g. Traffic, Summary) */
  tabFullName: string;
  /** DynamoDB SK / API param (e.g. Total, Summary) */
  subTabFullName: string;
}

/** @deprecated Use TabSubtabMapping for full mapping; kept for backward compatibility */
export interface TabSubtabFullNames {
  tabFullName: string;
  subTabFullName: string;
}

/**
 * Map from (tab param, subtab param) to full mapping: tab, subtab, tabFullName, subTabFullName.
 * Covers all combinations from useAtlasV2Routes (ATLAS_TABS, ATLAS_SUBTABS) normalized to
 * the canonical list: Summary/Summary, Traffic/Total|Organic|Paid|Other, Conversion/Conversion,
 * Sales/Unit Sold|Retail Sales, Prices/Prices|Promotions, Content/Content, Ratings/Ratings.
 */
const URL_TO_MAPPING: Record<string, Record<string, Omit<TabSubtabMapping, 'tab' | 'subtab'>>> = {
  scorecard: {
    all: { tabFullName: 'Summary', subTabFullName: 'Summary' }
  },
  scorecardV2: {
    all: { tabFullName: 'Summary', subTabFullName: 'Summary' }
  },
  traffic: {
    totalClicks: { tabFullName: 'Traffic', subTabFullName: 'Total' },
    organicClicks: { tabFullName: 'Traffic', subTabFullName: 'Organic' },
    adClicks: { tabFullName: 'Traffic', subTabFullName: 'Paid' }
  },
  trafficV2: {
    totalClicks: { tabFullName: 'Traffic', subTabFullName: 'Total' },
    organicClicks: { tabFullName: 'Traffic', subTabFullName: 'Organic' },
    otherClicks: { tabFullName: 'Traffic', subTabFullName: 'Other' },
    adClicks: { tabFullName: 'Traffic', subTabFullName: 'Paid' }
  },
  conversion: {
    purchaseRate: { tabFullName: 'Conversion', subTabFullName: 'Conversion' }
  },
  conversionV2: {
    purchaseRate: { tabFullName: 'Conversion', subTabFullName: 'Conversion' }
  },
  sales: {
    retailSales: { tabFullName: 'Sales', subTabFullName: 'Retail Sales' },
    unitsSold: { tabFullName: 'Sales', subTabFullName: 'Unit Sold' }
  },
  salesV2: {
    retailSales: { tabFullName: 'Sales', subTabFullName: 'Retail Sales' },
    unitsSold: { tabFullName: 'Sales', subTabFullName: 'Unit Sold' }
  },
  prices: {
    retailPrice: { tabFullName: 'Prices', subTabFullName: 'Prices' },
    promotions: { tabFullName: 'Prices', subTabFullName: 'Promotions' }
  },
  pricesV2: {
    retailPrice: { tabFullName: 'Prices', subTabFullName: 'Prices' },
    promotions: { tabFullName: 'Prices', subTabFullName: 'Promotions' }
  },
  content: {
    contentScore: { tabFullName: 'Content', subTabFullName: 'Content' }
  },
  contentV2: {
    contentScore: { tabFullName: 'Content', subTabFullName: 'Content' }
  },
  rating: {
    rating: { tabFullName: 'Ratings', subTabFullName: 'Ratings' }
  },
  ratingV2: {
    rating: { tabFullName: 'Ratings', subTabFullName: 'Ratings' }
  }
};

/**
 * Maps URL query params (tab, subtab) to the full mapping (tab, subtab, tabFullName, subTabFullName).
 * Use this to keep track of exact URL params and their DynamoDB full-name equivalents.
 * Returns null if the combination is not in the canonical list.
 */
export function mapUrlParamsToFullNames(tab: string, subtab: string): TabSubtabMapping | null {
  if (!tab || !subtab) return null;
  const tabKey = tab.trim();
  const subtabKey = subtab.trim();
  const byTab = URL_TO_MAPPING[tabKey];
  if (!byTab) return null;
  const full = byTab[subtabKey];
  if (!full) return null;
  return { tab: tabKey, subtab: subtabKey, tabFullName: full.tabFullName, subTabFullName: full.subTabFullName };
}

/**
 * Resolves a single `{{token}}` placeholder to a concrete value.
 * Checks `extraTokens` first (e.g. mainTimePeriod), then falls back to
 * entity-derived tokens via a switch. Each supported token is handled in a
 * switch so future tokens can be added with custom logic.
 *
 * @returns The replacement string, or `null` to leave the placeholder as-is.
 */
function resolveToken(
  token: string,
  mainEntity: Record<string, any> | null,
  extraTokens: Record<string, string>
): string | null {
  // Non-entity tokens (mainTimePeriod, etc.)
  if (extraTokens[token] !== undefined && extraTokens[token] !== '') {
    return extraTokens[token];
  }

  if (!mainEntity) return null;

  switch (token) {
    case 'brand':
      return mainEntity.brandName ? String(mainEntity.brandName) : null;
    case 'category':
      return mainEntity.categoryName ? String(mainEntity.categoryName) : null;
    case 'subcategory':
      return mainEntity.subCategoryName ? String(mainEntity.subCategoryName) : null;
    case 'retailerSku':
      return mainEntity.retailerSku ? String(mainEntity.retailerSku) : null;
    case 'entityName':
      return mainEntity.name ? String(mainEntity.name) : null;
    case 'entityId':
      return mainEntity.id != null ? String(mainEntity.id) : null;
    case 'entityType':
      return mainEntity.type ? String(mainEntity.type) : null;
    // Add future tokens here, e.g.:
    // case 'parentCategoryName':
    //   return mainEntity.parentCategoryName ? String(mainEntity.parentCategoryName) : null;
    default:
      return null;
  }
}

/**
 * Replaces `{{token}}` placeholders in a question template with dynamic values
 * derived from the current main entity and extra context tokens.
 * Unknown or unresolvable tokens are left as-is so PMs can spot missing data.
 *
 * @param extraTokens - Flat key-value map for non-entity tokens (e.g. mainTimePeriod).
 *
 * @example
 * interpolateQuestion(
 *   "Show me the trends in the {{entityName}} in the {{mainTimePeriod}}?",
 *   { name: "Bakeware", id: "1195", type: "category" },
 *   { mainTimePeriod: "past 13 weeks" }
 * )
 * // => "Show me the trends in the Bakeware in the past 13 weeks?"
 */
export function interpolateQuestion(
  template: string,
  mainEntity: Record<string, any> | null,
  extraTokens: Record<string, string> = {}
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, token: string) => {
    const value = resolveToken(token, mainEntity, extraTokens);
    return value !== null ? value : match;
  });
}
